// Lead Generation System — staff earnings review + weekly pay-period finalize.
// DISPLAY-ONLY: nothing here moves real money. Grade + bonus multiplier are
// AUTOMATIC (read-only); "Lock" freezes the figure for display/reporting only —
// the amount is paid off-system. Reads gate on LeadGen.View; the review/finalize
// actions gate on LeadGen.ReviewEarnings.

import { useEffect, useMemo, useState } from "react";
import { Lock, Unlock } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  SubmitButton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { calendarDateInputToIso, formatCalendarDate, formatPayWeekRange } from "@/design-system/lib/format";
import { useLeadGenerators } from "@/hooks/leadGen/useLeadGenerators";
import type { LeadGenGradeResultDto } from "@/api/leadGen/earnings";
import {
  useAddAdjustment,
  useEarningsSummary,
  useGeneratorWeekSummaries,
  useGrade,
  useLockPayPeriod,
  usePayPeriods,
} from "@/hooks/leadGen/useEarnings";
import { bucketLabel, money, todayInputDate } from "./leadGenShared";

const BUCKET_ORDER = ["outreach", "nda_cim", "interested_escalations", "appointments"];

export function EarningsReviewPage() {
  const { hasPermission } = useAuth();
  const canReview = hasPermission("LeadGen.ReviewEarnings");
  const toast = useToast();
  const onError = (err: unknown) => toast.error(extractErrorMessage(err));

  const generators = useLeadGenerators({ page: 1, pageSize: 200 });
  const [generatorId, setGeneratorId] = useState("");
  const [weekDate, setWeekDate] = useState(todayInputDate());
  const weekIso = useMemo(() => calendarDateInputToIso(weekDate) ?? "", [weekDate]);

  useEffect(() => {
    const first = generators.data?.data?.[0]?.id;
    if (!generatorId && first) setGeneratorId(first);
  }, [generators.data, generatorId]);

  const summary = useEarningsSummary(generatorId || null, weekIso);
  const payPeriods = usePayPeriods(generatorId || null);
  const grade = useGrade(generatorId || null, weekIso);
  const lockPeriod = useLockPayPeriod();

  const normalizedWeek = summary.data?.weekOf;
  const period = normalizedWeek
    ? payPeriods.data?.find((pp) => pp.weekOf === normalizedWeek)
    : undefined;

  const bucketByCode = useMemo(() => {
    const m = new Map(summary.data?.buckets.map((b) => [b.bucket, b]));
    return m;
  }, [summary.data]);
  const maxCount = Math.max(1, ...BUCKET_ORDER.map((c) => bucketByCode.get(c)?.count ?? 0));

  const canAct = canReview && !!generatorId && !!weekIso;

  return (
    <div className="flex flex-col gap-space-5">
      <PageHeader
        eyebrow="Lead Generation"
        title="Earnings review"
        description="Review a generator's weekly KPI earnings, then lock the pay period. Grade and the bonus multiplier are automatic. DISPLAY-ONLY — no money moves in the system; Lock finalizes the figure for reporting and it is paid off-system."
      />

      <div className="flex flex-wrap items-end gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Generator</span>
          <select
            className="h-10 w-64 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
            value={generatorId}
            onChange={(e) => setGeneratorId(e.target.value)}
          >
            <option value="">Select a generator…</option>
            {(generators.data?.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Week of</span>
          <Input
            type="date"
            value={weekDate}
            onChange={(e) => setWeekDate(e.target.value)}
            className="w-48"
          />
        </label>
        {normalizedWeek && (() => {
          const range = formatPayWeekRange(normalizedWeek);
          if (!range) return null;
          return (
            <div className="text-caption text-gray-mid pb-space-2 leading-relaxed">
              <div className="font-semibold text-onyx">Pay week: {range.rangeLabel}</div>
              <div>UTC: {range.utcLine}</div>
              <div>Pacific ({range.pacificAbbrev}): {range.pacificLine}</div>
            </div>
          );
        })()}
      </div>

      {!generatorId ? (
        <EmptyState title="Pick a generator" description="Choose a lead generator to review their weekly earnings." />
      ) : summary.isLoading ? (
        <Card>Loading summary…</Card>
      ) : summary.isError ? (
        <Card>
          <p className="text-small text-status-error" role="alert">
            {extractErrorMessage(summary.error)}
          </p>
        </Card>
      ) : summary.data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3">
            <MoneyCard label="Actions" value={String(summary.data.totalCount)} muted />
            <MoneyCard label="Earned" value={money(summary.data.totalEarned)} />
            <MoneyCard label="Credited" value={money(summary.data.totalCredited)} accent />
            <MoneyCard label="Capped out" value={money(summary.data.totalCapped)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-space-4">
            <Card className="lg:col-span-2">
              <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-3">
                Buckets &amp; funnel
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>Bucket</TH>
                    <TH className="text-right">Count</TH>
                    <TH>Funnel</TH>
                    <TH className="text-right">Earned</TH>
                    <TH className="text-right">Credited</TH>
                    <TH className="text-right">Capped</TH>
                    <TH className="text-right">Wk cap left</TH>
                  </TR>
                </THead>
                <TBody>
                  {BUCKET_ORDER.map((code) => {
                    const b = bucketByCode.get(code);
                    const count = b?.count ?? 0;
                    return (
                      <TR key={code}>
                        <TD className="font-semibold text-onyx">{bucketLabel(code)}</TD>
                        <TD className="text-right tabular-nums">{count}</TD>
                        <TD>
                          <div className="h-2 w-full max-w-[160px] rounded-pill bg-white-warm">
                            <div
                              className="h-2 rounded-pill bg-gold"
                              style={{ width: `${(count / maxCount) * 100}%` }}
                            />
                          </div>
                        </TD>
                        <TD className="text-right tabular-nums">{money(b?.earned ?? 0)}</TD>
                        <TD className="text-right tabular-nums">{money(b?.credited ?? 0)}</TD>
                        <TD className="text-right tabular-nums text-gray-mid">{money(b?.capped ?? 0)}</TD>
                        <TD className="text-right tabular-nums">{money(b?.weeklyCapRemaining)}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </Card>

            <Card>
              <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-3">
                Credited by state
              </h3>
              <div className="flex flex-col gap-space-3">
                <StateLine label="Estimated" tone="draft" value={money(summary.data.estimatedCredited)} />
                <StateLine label="Pending review" tone="review" value={money(summary.data.pendingReviewCredited)} />
                <StateLine label="Approved" tone="finalized" value={money(summary.data.approvedCredited)} />
              </div>
              <div className="mt-space-4 pt-space-3 border-t border-border-light grid grid-cols-2 gap-space-3">
                <SmallStat label="Weekly cap left" value={money(summary.data.weeklyTotalCapRemaining)} />
                <SmallStat label="Monthly cap left" value={money(summary.data.monthlyTotalCapRemaining)} />
              </div>
            </Card>
          </div>

          <PayPeriodPanel
            canReview={canReview}
            canAct={canAct}
            pending={lockPeriod.isPending}
            isLocked={!!period?.isLocked}
            grade={grade.data}
            onLockToggle={(lock) =>
              lockPeriod.mutate(
                { leadGeneratorId: generatorId, weekOf: weekIso, lock },
                { onSuccess: () => toast.success(lock ? "Pay period locked" : "Pay period unlocked"), onError },
              )
            }
          />

          {canReview && (
            <AdjustmentForm
              generatorId={generatorId}
              weekIso={weekIso}
              isLocked={!!period?.isLocked}
              onError={onError}
            />
          )}
        </>
      ) : null}

      <CrossGeneratorOversight
        ids={(generators.data?.data ?? []).filter((g) => !g.isArchived).map((g) => g.id)}
        names={new Map((generators.data?.data ?? []).map((g) => [g.id, g.displayName]))}
      />
    </div>
  );
}

// --- Pay-period finalize panel (auto grade, read-only) ---------------------

function PayPeriodPanel({
  canReview,
  canAct,
  pending,
  isLocked,
  grade,
  onLockToggle,
}: {
  canReview: boolean;
  canAct: boolean;
  pending: boolean;
  isLocked: boolean;
  grade: LeadGenGradeResultDto | undefined;
  onLockToggle: (lock: boolean) => void;
}) {
  return (
    <Card featured>
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
        <div>
          <h3 className="font-display text-h3 text-onyx m-0">Pay period</h3>
          <p className="text-caption text-gray-mid m-0">
            Grade &amp; the bonus multiplier are automatic (the multiplier is upside-only — never below 1.00×). Lock
            finalizes the figure for display &amp; reporting only — no money moves in the system; it is paid off-system.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {isLocked ? <Badge status="finalized">Locked</Badge> : <Badge status="draft">Open</Badge>}
          {grade?.letter && <Badge status="gold">Grade {grade.letter}</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-space-4 mb-space-3">
        <SmallStat label="Approved gross" value={money(grade?.approvedGross)} />
        <SmallStat label="Approved net" value={money(grade?.approvedNet)} />
        <SmallStat
          label="Bonus multiplier"
          value={grade ? `${grade.multiplier.toFixed(2)}×` : "—"}
        />
        <SmallStat
          label="Grade"
          value={grade ? `${grade.letter} · ${grade.gradePercent.toFixed(0)}%` : "—"}
        />
      </div>

      {grade && grade.nextLetter && grade.distanceToNext > 0 && (
        <p className="text-caption text-gray-mid mb-space-3">
          {money(grade.distanceToNext)} more approved KPI credits to reach grade {grade.nextLetter} (
          {grade.nextMultiplier.toFixed(2)}× bonus).
        </p>
      )}

      {!canReview ? (
        <p className="text-caption text-gray-mid">
          You have view access only. Locking a pay period requires LeadGen.ReviewEarnings.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-space-3">
          {isLocked ? (
            <Button variant="secondary" size="sm" disabled={!canAct || pending} onClick={() => onLockToggle(false)}>
              <Unlock size={14} strokeWidth={1.5} aria-hidden /> Unlock
            </Button>
          ) : (
            <Button size="sm" disabled={!canAct || pending} onClick={() => onLockToggle(true)}>
              <Lock size={14} strokeWidth={1.5} aria-hidden /> Lock (finalize)
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

// --- Manual adjustment (signed dollars on the current open week) -----------

function AdjustmentForm({
  generatorId,
  weekIso,
  isLocked,
  onError,
}: {
  generatorId: string;
  weekIso: string;
  isLocked: boolean;
  onError: (err: unknown) => void;
}) {
  const toast = useToast();
  const addAdjustment = useAddAdjustment();

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const amountNum = Number(amount);
  const amountInvalid = amount.trim() === "" || Number.isNaN(amountNum) || amountNum === 0;
  const reasonInvalid = reason.trim() === "";

  return (
    <Card>
      <h3 className="font-display text-label uppercase tracking-label text-gray-mid mb-space-1">
        Manual adjustment
      </h3>
      <p className="text-caption text-gray-mid mb-space-4">
        A signed-dollar correction that posts to the current OPEN pay week (a positive or negative amount). It is a
        display figure excluded from KPI caps and the grade base.
      </p>

      {isLocked ? (
        <p className="text-caption text-status-warning" role="status">
          This pay week is locked — unlock it to post an adjustment.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">Amount (signed)</span>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 25.00 or -10.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-40 text-right tabular-nums"
              aria-label="Adjustment amount"
            />
          </label>
          <label className="flex flex-col gap-space-1 flex-1 min-w-[240px]">
            <span className="text-caption uppercase tracking-wide text-gray-mid">Reason (required)</span>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <SubmitButton
            type="button"
            size="sm"
            pending={addAdjustment.isPending}
            pendingLabel="Recording…"
            disabled={amountInvalid || reasonInvalid}
            onClick={() =>
              addAdjustment.mutate(
                { leadGeneratorId: generatorId, weekOf: weekIso, amount: amountNum, reason: reason.trim() },
                {
                  onSuccess: () => {
                    toast.success("Adjustment recorded");
                    setAmount("");
                    setReason("");
                  },
                  onError,
                },
              )
            }
          >
            Record adjustment
          </SubmitButton>
        </div>
      )}
    </Card>
  );
}

// --- Cross-generator oversight ---------------------------------------------

function CrossGeneratorOversight({ ids, names }: { ids: string[]; names: Map<string, string> }) {
  const currentWeek = useMemo(() => calendarDateInputToIso(todayInputDate()) ?? "", []);
  const results = useGeneratorWeekSummaries(ids, currentWeek);

  return (
    <section>
      <h2 className="font-display text-h3 text-onyx mb-space-1">Cross-generator oversight</h2>
      <p className="text-caption text-gray-mid mb-space-3">
        Every active generator's current-week credited total ({formatCalendarDate(currentWeek)} week).
      </p>
      {ids.length === 0 ? (
        <EmptyState title="No generators" description="No active generators to summarize." />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Generator</TH>
              <TH className="text-right">Actions</TH>
              <TH className="text-right">Credited</TH>
              <TH className="text-right">Capped</TH>
              <TH className="text-right">Approved</TH>
            </TR>
          </THead>
          <TBody>
            {ids.map((id, i) => {
              const r = results[i];
              const d = r?.data;
              return (
                <TR key={id}>
                  <TD className="font-semibold text-onyx">{names.get(id) ?? id.slice(0, 8)}</TD>
                  <TD className="text-right tabular-nums">
                    {r?.isLoading ? "…" : d ? d.totalCount : "—"}
                  </TD>
                  <TD className="text-right tabular-nums">{r?.isLoading ? "…" : money(d?.totalCredited)}</TD>
                  <TD className="text-right tabular-nums text-gray-mid">
                    {r?.isLoading ? "…" : money(d?.totalCapped)}
                  </TD>
                  <TD className="text-right tabular-nums">{r?.isLoading ? "…" : money(d?.approvedCredited)}</TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </section>
  );
}

// --- Small presentational bits ---------------------------------------------

function MoneyCard({ label, value, accent, muted }: { label: string; value: string; accent?: boolean; muted?: boolean }) {
  return (
    <div className="border border-border-light rounded-md p-space-3 bg-white-pure">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className={`font-display text-h3 tabular-nums ${accent ? "text-gold-shadow" : muted ? "text-gray-mid" : "text-onyx"}`}>
        {value}
      </div>
    </div>
  );
}

function StateLine({ label, tone, value }: { label: string; tone: "draft" | "review" | "finalized"; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <Badge status={tone}>{label}</Badge>
      <span className="font-display text-body tabular-nums text-onyx">{value}</span>
    </div>
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="text-small font-semibold text-onyx tabular-nums">{value}</div>
    </div>
  );
}
